import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import ImageUploader from "@/components/app/ImageUploader";
import HoursEditor from "@/components/app/HoursEditor";
import LocationSearch from "@/components/app/LocationSearch";
import { defaultHours } from "@/lib/adega";

function Field({ label, children }) {
  return <div className="space-y-2"><Label>{label}</Label>{children}</div>;
}

export default function AdegaForm({ initial, onSaved }) {
  const editing = !!initial;
  const [form, setForm] = useState({
    name: initial?.name || "", responsible_name: initial?.responsible_name || "", description: initial?.description || "",
    whatsapp: initial?.whatsapp || "", photo_url: initial?.photo_url || null, hours: initial?.hours || defaultHours(),
    street: initial?.street || (initial && !initial.street ? initial.address : "") || "", number: initial?.number || "",
    neighborhood: initial?.neighborhood || "", approx_only: !!initial?.approx_only,
  });
  const [loc, setLoc] = useState(initial ? { lat: initial.lat, lng: initial.lng } : null);
  const [accept, setAccept] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const query = form.street && form.number ? `${form.street}, ${form.number}` : "";

  const submit = async (e) => {
    e.preventDefault();
    if (!loc?.lat) { toast.error("Confirme o pin da adega no mapa."); return; }
    if (!editing && !accept) { toast.error("Aceite os Termos de Uso e a Política de Privacidade."); return; }
    setSaving(true);
    try {
      const body = { ...form, lat: loc.lat, lng: loc.lng, accept_terms: editing ? undefined : accept };
      const { data } = editing ? await api.patch("/adegas/mine", body) : await api.post("/adegas", body);
      toast.success(editing ? "Dados da adega salvos." : "Adega cadastrada! Agora é só aguardar a aprovação.");
      onSaved?.(data);
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="adega-form">
      <ImageUploader value={form.photo_url} onChange={(v) => setForm((f) => ({ ...f, photo_url: v }))} square label="Foto do pin (fachada, logo ou balcão)" testid="adega-photo-uploader" aspect="aspect-square max-w-[160px] rounded-full" />
      <Field label="Nome da adega"><Input value={form.name} onChange={set("name")} required minLength={2} maxLength={60} placeholder="ex: Adega do Bairro" data-testid="adega-name-input" /></Field>
      <Field label="Nome do responsável"><Input value={form.responsible_name} onChange={set("responsible_name")} required minLength={2} maxLength={60} data-testid="adega-responsible-input" /></Field>
      <Field label="WhatsApp da loja"><Input value={form.whatsapp} onChange={set("whatsapp")} required inputMode="tel" placeholder="(14) 99999-9999" data-testid="adega-whatsapp-input" /></Field>
      <Field label="Descrição curta"><Textarea value={form.description} onChange={set("description")} maxLength={300} rows={2} placeholder="Gelada, entrega, estacionamento..." data-testid="adega-description-input" /></Field>

      <div className="rounded-xl border border-white/10 bg-muted/30 p-3 space-y-3">
        <div className="text-sm font-semibold">Endereço · Marília, SP</div>
        <div className="grid grid-cols-[1fr_88px] gap-2">
          <Input value={form.street} onChange={set("street")} required placeholder="Rua / Avenida" data-testid="adega-street-input" />
          <Input value={form.number} onChange={set("number")} required placeholder="Nº" data-testid="adega-number-input" />
        </div>
        <Input value={form.neighborhood} onChange={set("neighborhood")} required placeholder="Bairro" data-testid="adega-neighborhood-input" />
        <LocationSearch query={query} value={loc} onChange={setLoc} hint="Confira se o pin está na porta da sua adega." testid="adega-location" />
        <p className="text-[11px] text-muted-foreground">O endereço da adega é público, por ser um estabelecimento comercial.</p>
        <label className="flex items-center gap-3 text-sm">
          <Switch checked={form.approx_only} onCheckedChange={(v) => setForm((f) => ({ ...f, approx_only: v }))} data-testid="adega-approx-switch" />
          Mostrar só a região aproximada
        </label>
        {editing && <p className="text-[11px] text-muted-foreground">Mudanças de endereço passam por nova conferência do admin.</p>}
      </div>

      <Field label="Horário de funcionamento"><HoursEditor value={form.hours} onChange={(hours) => setForm((f) => ({ ...f, hours }))} /></Field>

      {!editing && (
        <div className="flex items-start gap-3">
          <Checkbox id="adega-accept" checked={accept} onCheckedChange={(v) => setAccept(!!v)} data-testid="adega-accept-checkbox" />
          <label htmlFor="adega-accept" className="text-sm text-muted-foreground leading-snug">
            Li e aceito os <Link to="/termos" className="text-primary underline">Termos de Uso</Link> e a <Link to="/privacidade" className="text-primary underline">Política de Privacidade</Link>.
          </label>
        </div>
      )}
      <div className="rounded-lg bg-muted/60 border border-border p-3 text-xs text-muted-foreground">Proibido vender para menores de 18 anos. Beba com moderação. Se beber, não dirija.</div>
      <Button type="submit" disabled={saving} className="w-full h-12 text-base press" data-testid="adega-save-button">
        {saving ? "Salvando..." : editing ? "Salvar alterações" : "Enviar para aprovação"}
      </Button>
    </form>
  );
}
